// Lecture du fichier `release` du serveur (markdown écrit par build.sh) : # titre, ## un bloc par mise en production, ### sous-listes.

export interface ReleaseField {
  label: string;
  html: string;
}

export interface ReleaseSection {
  title: string;
  items: string[];
}

export interface ReleaseEntry {
  version: string;
  date: string;
  level: string;
  fields: ReleaseField[];
  sections: ReleaseSection[];
  notes: string[];
}

export interface ReleaseJournal {
  title: string;
  intro: string[];
  /** Dans l'ordre du fichier : la plus ancienne d'abord, la dernière = celle en production. */
  entries: ReleaseEntry[];
}

export function parseReleaseJournal(text: string): ReleaseJournal {
  const journal: ReleaseJournal = { title: '', intro: [], entries: [] };
  let entry: ReleaseEntry | null = null;
  let section: ReleaseSection | null = null;
  // Lignes de texte consécutives = un seul paragraphe (markdown) ; une ligne vide ou un autre élément le ferme.
  let paragraph: string[] | null = null;
  const addText = (into: string[], line: string) => {
    if (paragraph === into) into[into.length - 1] += ' ' + inlineHtml(line);
    else into.push(inlineHtml(line));
    paragraph = into;
  };
  for (const raw of (text ?? '').split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || /^[-#]/.test(line)) paragraph = null;
    if (!line) continue;
    if (line.startsWith('### ')) {
      section = { title: line.slice(4).trim(), items: [] };
      entry?.sections.push(section);
    } else if (line.startsWith('## ')) {
      const [version = '', date = '', ...level] = line.slice(3).split(' — ').map(s => s.trim());
      entry = { version, date, level: level.join(' — '), fields: [], sections: [], notes: [] };
      section = null;
      journal.entries.push(entry);
    } else if (line.startsWith('# ')) {
      journal.title = line.slice(2).trim();
    } else if (line.startsWith('- ')) {
      const item = line.slice(2).trim();
      const field = /^\*\*(.+?)\s*:?\s*\*\*\s*(.*)$/.exec(item);
      if (section) section.items.push(inlineHtml(item));
      else if (entry && field) entry.fields.push({ label: field[1].replace(/\s*:$/, ''), html: inlineHtml(field[2]) });
      else if (entry) entry.notes.push(inlineHtml(item));
      else journal.intro.push(inlineHtml(item));
    } else {
      addText(entry ? entry.notes : journal.intro, line);
    }
  }
  return journal;
}

/** Le markdown en ligne du fichier (`code`, **gras**, *italique*), texte échappé d'abord. */
export function inlineHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*\s][^*]*)\*/g, '<em>$1</em>');
}
